import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from '../../util/reactIntl';
import secureLog from '../../util/secureLogger';
import css from './CustomErrorHandler.module.css';

/**
 * Custom error handler component that logs errors and provides auto-redirect
 */
const CustomErrorHandler = ({ error, redirectPath = '/browse', redirectDelay = 5000 }) => {
  const history = useHistory();
  
  // Log detailed error information
  useEffect(() => {
    secureLog.error('Page error encountered', {
      errorMessage: error?.message,
      errorStatus: error?.status,
      errorStack: error?.stack,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    });
    
    // Set up auto-redirect
    const redirectTimer = setTimeout(() => {
      history.push(redirectPath);
    }, redirectDelay);
    
    // Clean up timer if component unmounts
    return () => clearTimeout(redirectTimer);
  }, [error, redirectPath, redirectDelay, history]);
  
  // Calculate remaining seconds for countdown
  const [countdown, setCountdown] = React.useState(Math.floor(redirectDelay / 1000));
  
  useEffect(() => {
    if (countdown <= 0) return;
    
    const countdownTimer = setInterval(() => {
      setCountdown(prev => prev - 1);
    }, 1000);
    
    return () => clearInterval(countdownTimer);
  }, [countdown]);

  return (
    <div className={css.errorContainer}>
      <div className={css.errorContent}>
        <h2 className={css.errorTitle}>
          <FormattedMessage id="CustomErrorHandler.title" defaultMessage="We're sorry, something went wrong" />
        </h2>
        
        <p className={css.errorMessage}>
          <FormattedMessage 
            id="CustomErrorHandler.message" 
            defaultMessage="We're working on fixing this issue. You'll be redirected to the browse page in {countdown} seconds."
            values={{ countdown }}
          />
        </p>
        
        <button 
          className={css.redirectButton}
          onClick={() => history.push(redirectPath)}
        >
          <FormattedMessage id="CustomErrorHandler.redirectNow" defaultMessage="Go to browse page now" />
        </button>
      </div>
    </div>
  );
};

export default CustomErrorHandler; 